import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../modules';

export interface WatchlistItem {
  id: string;
  type: 'employee' | 'employer' | 'ptb';
  title: string;
  subtitle?: string;
  route: string;
  timestamp: number;
  note?: string; // User-added note for this item
  data?: any; // Store original data for reference
}

interface WatchlistContextType {
  items: WatchlistItem[];
  addItem: (item: Omit<WatchlistItem, 'timestamp'>) => void;
  removeItem: (id: string) => void;
  clearWatchlist: () => void;
  isInWatchlist: (id: string) => boolean;
  updateNote: (id: string, note: string) => void;
}

const WatchlistContext = createContext<WatchlistContextType | undefined>(undefined);

export const useWatchlist = () => {
  const context = useContext(WatchlistContext);
  if (!context) {
    throw new Error('useWatchlist must be used within a WatchlistProvider');
  }
  return context;
};

interface WatchlistProviderProps {
  children: ReactNode;
}

export const WatchlistProvider: React.FC<WatchlistProviderProps> = ({ children }) => {
  const [items, setItems] = useState<WatchlistItem[]>([]);

  // Get current user's admin ID to make watchlist user-specific
  const currentUserId = useSelector((state: RootState) => state.user.aid);

  // Create user-specific localStorage key
  const watchlistKey = useMemo(() => {
    return currentUserId ? `paywatch-watchlist-${currentUserId}` : 'paywatch-watchlist-guest';
  }, [currentUserId]);

  // Load watchlist from localStorage on mount or when user changes
  useEffect(() => {
    try {
      const savedWatchlist = localStorage.getItem(watchlistKey);
      if (savedWatchlist) {
        setItems(JSON.parse(savedWatchlist));
      } else {
        // Clear items if no saved watchlist for this user
        setItems([]);
      }
    } catch (error) {
      console.error('Error loading watchlist:', error);
      setItems([]);
    }
  }, [watchlistKey]);

  // Save to localStorage whenever items change
  useEffect(() => {
    try {
      localStorage.setItem(watchlistKey, JSON.stringify(items));
    } catch (error) {
      console.error('Error saving watchlist:', error);
    }
  }, [items, watchlistKey]);

  const addItem = (newItem: Omit<WatchlistItem, 'timestamp'>) => {
    setItems((prevItems) => {
      // Check if item already exists
      const existingIndex = prevItems.findIndex((item) => item.id === newItem.id);

      if (existingIndex !== -1) {
        // Update existing item (move to top)
        const updatedItems = [...prevItems];
        updatedItems[existingIndex] = { ...newItem, timestamp: Date.now() };
        return updatedItems.sort((a, b) => b.timestamp - a.timestamp);
      }
      // Add new item
      const itemWithTimestamp = { ...newItem, timestamp: Date.now() };
      return [itemWithTimestamp, ...prevItems].slice(0, 20); // Keep only 20 most recent items
    });
  };

  const removeItem = (id: string) => {
    setItems((prevItems) => prevItems.filter((item) => item.id !== id));
  };

  const clearWatchlist = () => {
    setItems([]);
    // Also remove from localStorage
    try {
      localStorage.removeItem(watchlistKey);
    } catch (error) {
      console.error('Error clearing watchlist from localStorage:', error);
    }
  };

  // Clear watchlist when user logs out (currentUserId becomes null/undefined)
  useEffect(() => {
    if (!currentUserId) {
      setItems([]);
    }
  }, [currentUserId]);

  const isInWatchlist = (id: string) => {
    return items.some((item) => item.id === id);
  };

  const updateNote = (id: string, note: string) => {
    setItems((prevItems) =>
      prevItems.map((item) => (item.id === id ? { ...item, note: note.trim() || undefined } : item))
    );
  };

  const value = useMemo(
    () => ({
      items,
      addItem,
      removeItem,
      clearWatchlist,
      isInWatchlist,
      updateNote,
    }),
    [items]
  );

  return <WatchlistContext.Provider value={value}>{children}</WatchlistContext.Provider>;
};
